import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";
import { Agent } from "../types";

/* ------------------------------------------------------------------ */
/* Types / helpers                                                    */
/* ------------------------------------------------------------------ */
export interface AgentsListResponse {
  results: Agent[];
  meta: {
    limit: number;
    offset: number;
    count: number;
  };
  links: {
    first: string;
    last: string;
    prev?: string;
    next?: string;
  };
}

export type Params = {
  searchTerm: string;
};

function isEqual(a: Params, b: Params) {
  return a.searchTerm === b.searchTerm;
}

/* ------------------------------------------------------------------ */
/* SUSPENDING atom family                                             */
/* ------------------------------------------------------------------ */

const agentsSearchAtomFamily_SUSPENDS = atomFamily(({ searchTerm }: Params) => {
  // If there's no query, return an empty list right away to avoid an
  // unnecessary network request (and a brief "loading" state in loadable
  // consumers).
  if (searchTerm.trim() === "") {
    const emptyAtom = atom<Promise<Agent[]>>(Promise.resolve([]));
    emptyAtom.debugLabel =
      "agentsSearchAtomFamily_SUSPENDS(searchTerm:<empty>)";
    return emptyAtom;
  }

  const baseAtom = atom<Promise<Agent[]>>(async () => {
    try {
      const query = searchTerm
        ? `?search=${encodeURIComponent(searchTerm)}`
        : "";
      const res = await fetch(`/api/canvasui/agents${query}`);

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.errors ?? res.statusText);
      }

      const json = (await res.json()) as AgentsListResponse;
      return json.results as AgentsListResponse["results"];
    } catch (error) {
      console.error(
        `Error fetching Agents${
          searchTerm ? ` with "${searchTerm}" searchTerm` : ""
        }`,
        error,
      );
      throw error;
    }
  });

  baseAtom.debugLabel = `agentsSearchAtomFamily_SUSPENDS(searchTerm:${searchTerm})`;
  return baseAtom;
}, isEqual);

/* ------------------------------------------------------------------ */
/* NON-suspending (loadable) atom family                              */
/* ------------------------------------------------------------------ */

export const agentsSearchAtomFamily = atomFamily((params: Params) => {
  const base = agentsSearchAtomFamily_SUSPENDS(params);
  return loadable(base);
}, isEqual);
